import { filledInputClasses } from '@mui/material/FilledInput';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { alpha, Theme } from '@mui/material/styles';

import { ALPHA_OPACITY } from '../constants';

type RootProps = {
  ownerState: {
    color: keyof Theme['palette'];
  };
};

type ColorPreset = {
  contrastText: string;
  dark: string;
  darker: string;
  light: string;
  lighter: string;
  main: string;
};

export function textField(theme: Theme) {
  const color = {
    focused: theme.palette.text.primary,
    active: theme.palette.text.secondary,
    placeholder: theme.palette.text.disabled,
  };

  const font = {
    label: theme.typography.body1,
    value: theme.typography.body2,
  };

  return {
    // HELPER
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: theme.spacing(1),
        },
      },
    },

    // LABEL
    MuiFormLabel: {
      styleOverrides: {
        root: ({ ownerState }: RootProps) => {
          const ownerPaletteColor =
            ownerState.color === 'primary'
              ? undefined
              : (theme.palette[ownerState.color] as ColorPreset);
          return {
            ...font.value,
            color: theme.palette.grey[600], // Use the light color of the palette
            [`&.${inputLabelClasses.shrink}`]: {
              ...font.label,
              fontWeight: 600,
              color: ownerPaletteColor?.main ?? color.active, // Use the main color of the palette
              [`&.${inputLabelClasses.focused}`]: {
                color: ownerPaletteColor?.main ?? color.focused, // Use the dark color for focused state
              },
              [`&.${inputLabelClasses.error}`]: {
                color: theme.palette.error.main, // Error state color
              },
              [`&.${inputLabelClasses.disabled}`]: {
                color: alpha(theme.palette.text.disabled, ALPHA_OPACITY.DISABLED_LABEL), // Disabled state color
              },
              [`&.${inputLabelClasses.filled}`]: {
                transform: 'translate(12px, 6px) scale(0.75)', // Transform for filled state
              },
            },
          };
        },
      },
    },

    // FORM CONTROL - readOnly label color override
    MuiFormControl: {
      styleOverrides: {
        root: {
          [`&:has(.${inputBaseClasses.readOnly})`]: {
            [`& .${inputLabelClasses.shrink}.${inputLabelClasses.focused}`]: {
              color: color.active,
            },
          },
        },
      },
    },

    // BASE
    MuiInputBase: {
      styleOverrides: {
        root: {
          [`&.${inputBaseClasses.disabled}`]: {
            '& svg': {
              color: theme.palette.text.disabled, // Color for disabled state
            },
          },
        },
        input: {
          ...font.value,
          '&::placeholder': {
            opacity: 1,
            color: color.placeholder, // Use placeholder color
          },
          [`&.${inputBaseClasses.disabled}`]: {
            color: alpha(theme.palette.text.disabled, ALPHA_OPACITY.DISABLED_LABEL), // Disabled value color
            WebkitTextFillColor: alpha(theme.palette.text.disabled, ALPHA_OPACITY.DISABLED_LABEL),
          },
          [`&.${inputBaseClasses.readOnly}`]: {
            cursor: 'default',
          },
        },
      },
    },

    // STANDARD
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: alpha(theme.palette.grey[500], ALPHA_OPACITY.DISABLED), // Standard underline before color
          },
          '&:after': {
            borderBottomColor: color.focused, // Underline color when focused
          },
        },
      },
    },

    // OUTLINED
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }: RootProps) => {
          const ownerPaletteColor =
            ownerState.color === 'primary'
              ? undefined
              : (theme.palette[ownerState.color] as ColorPreset);
          return {
            [`&.${outlinedInputClasses.root}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderWidth: ownerPaletteColor ? 2 : undefined,
                borderColor: ownerPaletteColor ? ownerPaletteColor.main : undefined, // Outlined color when focused
              },
            },
            [`&.${outlinedInputClasses.focused}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: ownerPaletteColor?.main ?? color.focused, // Outlined color when focused
              },
            },
            [`&.${outlinedInputClasses.error}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderWidth: 2,
                borderColor: theme.palette.error.main, // Outlined color when error
              },
            },
            [`&.${outlinedInputClasses.disabled}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: theme.palette.action.disabledBackground, // Outlined color when disabled
              },
            },
            [`&.${inputBaseClasses.readOnly}`]: {
              '&:hover': {
                [`& .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: theme.palette.action.base,
                },
              },
              [`&.${outlinedInputClasses.focused}`]: {
                [`& .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: theme.palette.action.base,
                  borderWidth: 1,
                },
              },
            },
          };
        },
        notchedOutline: {
          borderColor: theme.palette.action.base, // Default notched outline color
          transition: theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
        },
      },
    },

    // FILLED
    MuiFilledInput: {
      styleOverrides: {
        root: ({ ownerState }: RootProps) => {
          const ownerPaletteColor = theme.palette[ownerState.color] as ColorPreset;
          return {
            borderRadius: theme.shape.borderRadius, // Border radius for filled input
            backgroundColor: alpha(theme.palette.grey[500], 0.08), // Background color for filled input
            '&:hover': {
              backgroundColor: alpha(theme.palette.grey[500], 0.16), // Hover background color
            },
            [`&.${filledInputClasses.focused}`]: {
              backgroundColor: alpha(ownerPaletteColor.main, 0.16), // Filled input color when focused
            },
            [`&.${filledInputClasses.error}`]: {
              backgroundColor: alpha(theme.palette.error.main, 0.08), // Error background color
              [`&.${filledInputClasses.focused}`]: {
                backgroundColor: alpha(theme.palette.error.main, 0.16), // Error background color when focused
              },
            },
            [`&.${filledInputClasses.disabled}`]: {
              backgroundColor: theme.palette.action.disabledBackground, // Disabled background color
            },
          };
        },
      },
    },
  };
}
